export interface GuardUser {
  role?: {
    type?: string | null;
  } | null;
  child?: {
    validLicense?: boolean | null;
    is_active?: boolean | null;
  } | null;
}

export interface AuthGuardContext {
  isAuthenticated: boolean;
  routeName: string;
  path: string;
  requiresAuth?: boolean;
  currentUser?: GuardUser | null;
  schoolRoles?: string[];
}

export type AuthGuardDecision =
  | { allow: true }
  | {
      allow: false;
      redirectTo:
        | 'users.signin'
        | 'school.login'
        | 'dashboard'
        | 'courses'
        | 'school.dashboard'
        | 'school.teacher.home';
    };

// Password recovery has to be here as well as in PUBLIC_AUTH_ROUTE_NAMES below:
// these live under /school, and the school branch of the guard bounces every
// unauthenticated /school/* path that is not a school auth target — which would
// make the link in the reset email land on the login screen.
const SCHOOL_AUTH_ROUTE_NAMES = new Set([
  'school.login',
  'school.register',
  'school.password.forgot',
  'school.password.reset',
]);
const SCHOOL_AUTH_PATHS = new Set([
  '/school/login',
  '/school/register',
  '/school/password/forgot',
  '/school/password/reset',
]);
const PUBLIC_AUTH_ROUTE_NAMES = new Set([
  'users.signin',
  'users.register',
  'users.child.login',
  'users.password.reset',
  'users.password.forgot',
  'users.presentation',
  'users.connect',
  ...SCHOOL_AUTH_ROUTE_NAMES,
]);
const PUBLIC_AUTH_PATHS = new Set([
  '/users/signin',
  '/users/child/login',
  '/users/password/reset',
  '/users/password/forgot',
  '/users/presentation',
  ...SCHOOL_AUTH_PATHS,
]);
const CHILD_FREE_COURSE_ROUTE_NAMES = new Set([
  'dashboard',
  'settings.profile',
  'avatar.manage',
  'courses',
  'course.my',
  'course.favorites',
  'course.sections',
  'course.sections.section',
  'course.step',
]);

export function normalizeRouteName(name: unknown): string {
  return typeof name === 'string' ? name : '';
}

/**
 * Which brand's front door a hostname belongs to.
 *
 * The two flows are sold as separate products (FlashStudies for schools, SET App
 * for parent/child) but ship as one codebase behind two DNS names. DNS can only
 * point a hostname at the app — it cannot point it at a path — so the hostname is
 * what decides which login page `/` opens.
 *
 * Hostnames come from `VITE_SCHOOL_HOSTS` (comma-separated, case-insensitive, an
 * optional `:port` is ignored). Nothing is hardcoded: until the customer's domain
 * exists the list is empty and every host lands on the parent flow, exactly as it
 * did before. This only picks a front door — it grants nothing, so a wrong entry
 * costs a redirect, never access.
 */
export function isSchoolHost(
  hostname: string,
  configuredHosts: string | undefined,
): boolean {
  if (!hostname || !configuredHosts) return false;

  const normalized = hostname.trim().toLowerCase();
  return configuredHosts
    .split(',')
    .map((host) => host.trim().toLowerCase().replace(/:\d+$/, ''))
    .filter(Boolean)
    .includes(normalized);
}

/** The product name shown in the tab and window title for a given hostname. */
export function brandName(
  hostname: string,
  configuredHosts: string | undefined,
): 'FlashStudies' | 'SET App' {
  return isSchoolHost(hostname, configuredHosts) ? 'FlashStudies' : 'SET App';
}

export function isSchoolUser(user: GuardUser | null | undefined): boolean {
  const roleType = user?.role?.type;
  return !!(
    roleType === 'school_admin' ||
    roleType === 'teacher' ||
    roleType === 'student'
  );
}

export function getSchoolDefaultRouteName(
  user: GuardUser | null | undefined,
): 'school.dashboard' | 'school.teacher.home' | 'dashboard' {
  const roleType = user?.role?.type;
  if (roleType === 'school_admin') {
    return 'school.dashboard';
  }
  if (roleType === 'teacher') {
    return 'school.teacher.home';
  }
  // Students have no class-viewing home; the dashboard is their landing page.
  return 'dashboard';
}

export function getSchoolRole(
  user: GuardUser | null | undefined,
): string | null {
  const roleType = user?.role?.type;
  if (roleType === 'school_admin') return 'school_admin';
  if (roleType === 'teacher') return 'teacher';
  if (roleType === 'student') return 'student';
  if (roleType === 'admin') return 'admin';
  return null;
}

export function isSchoolPath(path: string): boolean {
  return path.startsWith('/school');
}

export function isSchoolAuthRoute(routeName: string, path: string): boolean {
  return SCHOOL_AUTH_ROUTE_NAMES.has(routeName) || SCHOOL_AUTH_PATHS.has(path);
}

export function isPublicAuthRoute(routeName: string, path: string): boolean {
  return PUBLIC_AUTH_ROUTE_NAMES.has(routeName) || PUBLIC_AUTH_PATHS.has(path);
}

export function isAuthEntryRoute(routeName: string): boolean {
  return (
    routeName === 'users.signin' ||
    routeName === 'users.register' ||
    routeName === 'users.child.login' ||
    routeName === 'school.login' ||
    routeName === 'school.register'
  );
}

export function isChildWithoutValidLicense(
  user: GuardUser | null | undefined,
): boolean {
  return !!(user?.role?.type === 'child' && !user?.child?.validLicense);
}

export function isChildFreeCourseRoute(
  routeName: string,
  path: string,
): boolean {
  return (
    CHILD_FREE_COURSE_ROUTE_NAMES.has(routeName) ||
    path === '/courses' ||
    path.startsWith('/course/')
  );
}

export function getUserDefaultRouteName(
  user: GuardUser | null | undefined,
): 'dashboard' | 'courses' {
  return isChildWithoutValidLicense(user) ? 'courses' : 'dashboard';
}

export function decideAuthGuardRedirect(
  context: AuthGuardContext,
): AuthGuardDecision {
  const {
    isAuthenticated,
    routeName,
    path,
    requiresAuth = true,
    currentUser,
    schoolRoles = [],
  } = context;

  const isSchoolAuthTarget = isSchoolAuthRoute(routeName, path);
  const isPublicAuthTarget = isPublicAuthRoute(routeName, path);

  if (isSchoolPath(path) && !isAuthenticated && !isSchoolAuthTarget) {
    return { allow: false, redirectTo: 'school.login' };
  }

  if (requiresAuth && !isAuthenticated && !isPublicAuthTarget) {
    return { allow: false, redirectTo: 'users.signin' };
  }

  if (isAuthEntryRoute(routeName) && isAuthenticated) {
    return isSchoolUser(currentUser)
      ? { allow: false, redirectTo: getSchoolDefaultRouteName(currentUser) }
      : { allow: false, redirectTo: getUserDefaultRouteName(currentUser) };
  }

  if (
    isChildWithoutValidLicense(currentUser) &&
    !isChildFreeCourseRoute(routeName, path)
  ) {
    return { allow: false, redirectTo: 'courses' };
  }

  if (isAuthenticated && isSchoolPath(path) && !isSchoolUser(currentUser)) {
    return { allow: false, redirectTo: 'dashboard' };
  }

  if (schoolRoles.length > 0) {
    const currentSchoolRole = getSchoolRole(currentUser);
    if (!currentSchoolRole || !schoolRoles.includes(currentSchoolRole)) {
      return {
        allow: false,
        redirectTo: getSchoolDefaultRouteName(currentUser),
      };
    }
  }

  if (
    currentUser?.role?.type === 'child' &&
    !currentUser?.child?.is_active &&
    routeName !== 'dashboard'
  ) {
    return { allow: false, redirectTo: 'dashboard' };
  }

  return { allow: true };
}
